import { Box } from '@mui/material';
import config from 'config';
import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { palette, spaces } from '../../assets/styles/themes/tokens';
import MasterLayout from '../../components/AppLayout/MasterLayout';
import CoursesCarousel from '../../components/Dashboard/CoursesCarousel';
import CoursesRow from '../../components/Dashboard/CoursesRow/CoursesRow';
import MukiActionCarousel from '../../components/Dashboard/MukiActionCarousel';
import PinnedDocumentsCarousel from '../../components/Dashboard/PinnedDocumentsCarousel/PinnedDocumentsCarousel';
import PinnedRow from '../../components/Dashboard/PinnedRow/PinnedRow';
import ProfileStudyflixRow from '../../components/Dashboard/ProfileStudyflixRow/ProfileStudyflixRow';
import ProfileSubjectRow from '../../components/Dashboard/ProfileSubjectRow/ProfileSubjectRow';
import RecentlyViewedCarousel from '../../components/Dashboard/RecentlyViewedCarousel/RecentlyViewedCarousel';
import StudyflixCarousel from '../../components/Dashboard/StudyflixCarousel';
import SubjectCarousel from '../../components/Dashboard/SubjectCarousel';
import WorksheetRow from '../../components/Dashboard/WorksheetRow/WorksheetRow';
import DashboardPageHeader from '../../components/DashboardPageHeader';
import { DashboardPromoBanner } from '../../components/PromoBanner';
import { routes } from '../../constants';
import { selectIsOnboardingCompleted, selectUser } from '../../reducers/user.selectors';
import services from '../../services';
import { useAmplitudeExperiment } from '../../utils/ampli';
import { mapSchoolStagesToClassYears } from '../../utils/profileFilters';
import useRouter from '../../utils/useRouter';
import useShouldShowAIAssistant from '../../utils/useShouldShowAIAssistant';
import useFetchDashboardSubjectsProducts from './hooks/useFetchDashboardSubjects';

// Up-to-three profile subjects feed the treatment rows.
const MAX_PROFILE_SUBJECTS = 3;

const NewDashboardPage: React.FC = () => {
	const { router, match } = useRouter();
	const user = useSelector(selectUser);
	const isOnboardingCompleted = useSelector(selectIsOnboardingCompleted);

	// Shared `dashboard` experiment (key owned by PRO-5). Treatment replaces the
	// legacy per-subject carousels + old Studyflix carousel + Didaktik row with the
	// Muki action cards (PRO-5) and the profile-aware Studyflix + subject rows
	// (PRO-4). A treatment user who can't use Muki would get a near-empty new
	// dashboard, so gate on Muki-eligibility too and otherwise keep the legacy view.
	const { isReady, variant } = useAmplitudeExperiment(user, config.amplitudeExperiments.dashboard);
	const shouldShowAIAssistant = useShouldShowAIAssistant();
	const showNewDashboard = isReady && variant === 'treatment' && shouldShowAIAssistant;

	// On the new dashboard the profile-aware rows own their fetching — suppress the
	// legacy product fetches to avoid duplicate requests. Gate only on `!showNewDashboard`
	// (not `isReady`): control users are the majority, so we must not serialize their
	// subject rows behind the Amplitude round-trip (up to the 10s SDK timeout on a hard
	// load). The cost is that an eventual treatment user fires one legacy request burst
	// while the experiment is still resolving.
	const subjects = useFetchDashboardSubjectsProducts(!showNewDashboard);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (!isOnboardingCompleted) {
			router.replace(`/${routes.onboardingPage.root}?step=roles`);
		}
	}, [isOnboardingCompleted]);

	// Fires once per route entry, only after the async experiment resolves to the
	// new dashboard actually being shown. The ref keeps rerenders (profile updates,
	// subject fetches) from re-emitting; a remount is a new route entry and fires again.
	const hasTrackedDashboardViewed = useRef(false);
	useEffect(() => {
		if (showNewDashboard && isOnboardingCompleted && !hasTrackedDashboardViewed.current) {
			hasTrackedDashboardViewed.current = true;
			services.track.ampliTrackDashboardViewedEvent();
		}
	}, [showNewDashboard, isOnboardingCompleted]);

	if (!user) return null;
	// The onboarding flow's final step lands here with this param — it marks the
	// very first dashboard visit and switches the greeting to the welcome copy.
	const isFirstLogin = match.location.query?.isOnboardingComplete === 'true';
	const hasSubjects = subjects.userSubjects.length !== 0;
	const initialSubjects = subjects.userSubjects.slice(0, 2);
	const remainingSubjects = subjects.userSubjects.slice(2);

	const topSubjects = subjects.userSubjects.slice(0, MAX_PROFILE_SUBJECTS);
	// Agreed hierarchy: priority subject 1 sits between the worksheet row and
	// Studyflix; subjects 2-3 follow Recently viewed.
	const [primarySubject, ...secondarySubjects] = topSubjects;
	// The worksheet row spans every configured subject, not only the display top-three.
	const allSubjectIds = subjects.userSubjects.map((subject) => subject.id);
	const classYears = mapSchoolStagesToClassYears(user.schoolStages);
	const schoolType = user.schoolType;

	return (
		<MasterLayout forceNewNavigation>
			<Box
				sx={{
					backgroundColor: palette.background.paperElevation1,
					minHeight: '100vh',
				}}
			>
				<Box
					sx={{
						paddingTop: spaces.sm,
						paddingLeft: spaces.md,
						paddingBottom: spaces.xxxxl,
						display: 'flex',
						flexDirection: 'column',
						gap: spaces.lg,
					}}
				>
					<DashboardPageHeader firstName={user?.firstName} isFirstLogin={isFirstLogin} />
					<DashboardPromoBanner isEligible={showNewDashboard} />
					{showNewDashboard ? (
						<>
							<MukiActionCarousel />
							{hasSubjects && <WorksheetRow subjectIds={allSubjectIds} />}
							{primarySubject && (
								<ProfileSubjectRow
									key={primarySubject.id}
									subject={primarySubject}
									classYears={classYears}
									schoolType={schoolType}
								/>
							)}
							{hasSubjects && (
								<ProfileStudyflixRow
									subjects={topSubjects}
									classYears={classYears}
									schoolType={schoolType}
								/>
							)}
							<RecentlyViewedCarousel />
							{secondarySubjects.map((subject) => (
								<ProfileSubjectRow
									key={subject.id}
									subject={subject}
									classYears={classYears}
									schoolType={schoolType}
								/>
							))}
							<PinnedRow />
							<CoursesRow />
						</>
					) : (
						<>
							{hasSubjects &&
								initialSubjects.map((subject) => <SubjectCarousel key={subject.id} subject={subject} />)}
							<StudyflixCarousel />
							{remainingSubjects.length
								? remainingSubjects.map((subject) => <SubjectCarousel key={subject.id} subject={subject} />)
								: undefined}
							{subjects.defaultSubject && <SubjectCarousel subject={subjects.defaultSubject} />}
							<PinnedDocumentsCarousel />
							<CoursesCarousel />
						</>
					)}
				</Box>
			</Box>
		</MasterLayout>
	);
};

export default NewDashboardPage;
